/**
 * Life4Earth V2 — User Capability Registry API Adapter
 *
 * Frontend client for the user↔skill registry (DEV-1378, Content
 * Verification Stage 2). See SPEC_CONTENT_VERIFICATION.md §4 Stage 2 / §6.
 *
 * Credibility ladder: self-declared = discovery only; merit-rooted
 * endorsements (counted per endorser tier) = the primary domain gate;
 * SME designation = top rung.
 *
 * Talks to:
 *   GET    /api/users/{userGuid}/skills    → a user's skills + endorsement counts
 *   POST   /api/user-skills                → add a skill to your own profile
 *   DELETE /api/user-skills/{userSkillGuid}→ remove your own skill
 *   POST   /api/user-skills/endorse        → endorse another user's skill
 *   POST   /api/user-skills/sme-designate  → sysadmin SME toggle
 */

import { apiFetch } from './client';

// ── Types ──────────────────────────────────────────────────────────

/** Per-tier endorsement counts, e.g. { silver: 3, gold: 1 }. */
export type EndorsementCountsByTier = Partial<Record<
  'bronze' | 'silver' | 'gold' | 'platinum',
  number
>>;

/** A user's skill with credibility-ladder context. */
export interface UserSkill {
  userSkillGuid: string;
  userGuid: string;
  slug: string;
  label: string;
  domain?: string | null;
  selfDeclared: boolean;
  isSme: boolean;
  proficiency?: string | null;
  endorsementCountsByTier: EndorsementCountsByTier;
  totalEndorsements: number;
  createdAt: string;
}

/** Refreshed per-tier counts returned by the endorse endpoint. */
export interface EndorsementCounts {
  userGuid: string;
  skillSlug: string;
  endorsementCountsByTier: EndorsementCountsByTier;
  totalEndorsements: number;
}

// ── API Functions ──────────────────────────────────────────────────

/** Fetch a user's skills (public read). */
export function fetchUserSkills(userGuid: string): Promise<UserSkill[]> {
  return apiFetch<UserSkill[]>(`/api/users/${userGuid}/skills`);
}

/**
 * Add a skill to the CALLER's own profile. `skillSlug` must be a canonical
 * platform_skills slug (free text is rejected by the API).
 */
export function addUserSkill(
  skillSlug: string,
  proficiency?: string,
): Promise<UserSkill> {
  return apiFetch<UserSkill>('/api/user-skills', {
    method: 'POST',
    body: JSON.stringify({ skillSlug, proficiency }),
  });
}

/** Remove one of the caller's OWN skills. */
export function removeUserSkill(userSkillGuid: string): Promise<void> {
  return apiFetch<void>(`/api/user-skills/${userSkillGuid}`, {
    method: 'DELETE',
  });
}

/**
 * Endorse another user's skill. The caller is the endorser; their merit tier
 * is computed and snapshotted server-side. Requires >= Silver standing and
 * that the endorsee already holds the skill. Returns the refreshed counts.
 */
export function endorseSkill(
  userGuid: string,
  skillSlug: string,
): Promise<EndorsementCounts> {
  return apiFetch<EndorsementCounts>('/api/user-skills/endorse', {
    method: 'POST',
    body: JSON.stringify({ userGuid, skillSlug }),
  });
}

/** SystemAdmin-only: set or clear the SME (top-rung) designation. */
export function designateSme(
  userGuid: string,
  skillSlug: string,
  isSme: boolean,
): Promise<UserSkill> {
  return apiFetch<UserSkill>('/api/user-skills/sme-designate', {
    method: 'POST',
    body: JSON.stringify({ userGuid, skillSlug, isSme }),
  });
}
